import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { ChalkboardTeacher, ArrowLeft, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { projects } from "@/pages/ProjectsPage";
import ClassWeakSpots from "@/components/teacher/ClassWeakSpots";
import TeacherCsvButton from "@/components/teacher/TeacherCsvButton";
import ClassManager from "@/components/teacher/ClassManager";

function Stat({ label, value, testId }) {
  return (
    <div className="bg-[#121214]/80 border border-white/10 rounded-xl px-4 py-3" data-testid={testId}>
      <p className="text-[#71717A] text-[10px] uppercase tracking-[0.18em]">{label}</p>
      <p className="font-['JetBrains_Mono'] text-2xl text-white">{value}</p>
    </div>
  );
}

function Cell({ n, size }) {
  const cls = n === 0 ? "text-[#3F3F46]" : n === size ? "bg-emerald-500/15 text-emerald-300" : "bg-[#F5A524]/10 text-[#F5A524]";
  return <span className={`inline-block min-w-[2.6rem] text-center rounded-md px-1.5 py-0.5 font-['JetBrains_Mono'] text-xs ${cls}`}>{n}/{size}</span>;
}

export default function TeacherProgressPage() {
  const { user } = useAuth();
  const { lang } = useTranslation();
  const es = lang === "es";
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [classId, setClassId] = useState("");
  const [classes, setClasses] = useState([]);
  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    setData(null);
    axios
      .get("/api/admin/project-progress", { params: classId ? { class_id: classId } : {}, withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], sizes: {}, items_total: 0 }));
  }, [isAdmin, classId]);

  const items = (projects[lang] || projects.en).items;
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.rows || []).filter((r) => !s || r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s));
  }, [data, q]);

  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  const all = data?.rows || [];
  const active = all.filter((r) => r.items_done > 0).length;
  const finishedOne = all.filter((r) => r.projects_done > 0).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="teacher-progress-page">
      <Link to="/presenter">
        <Button variant="ghost" className="text-[#A1A1AA] hover:text-white hover:bg-white/5 -ml-3" data-testid="teacher-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Presenter
        </Button>
      </Link>
      <div>
        <p className="flex items-center gap-1.5 text-[#F5A524] text-xs uppercase tracking-[0.2em] mb-2">
          <ChalkboardTeacher weight="fill" className="w-4 h-4" />
          Admin
        </p>
        <h1 className="font-['Outfit'] text-4xl sm:text-5xl text-white font-semibold tracking-tight mb-2">
          {es ? "Progreso de Estudiantes" : "Teacher Progress View"}
        </h1>
        <p className="text-[#71717A] text-base">
          {es ? "Avance de cada estudiante en las listas de entregables de los proyectos." : "Each student's progress on the project deliverables checklists."}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={es ? "Estudiantes" : "Students"} value={all.length} testId="teacher-stat-students" />
        <Stat label={es ? "Con progreso" : "With progress"} value={active} testId="teacher-stat-active" />
        <Stat label={es ? "Terminaron 1+ proyecto" : "Finished 1+ project"} value={finishedOne} testId="teacher-stat-finished" />
        <Stat label={es ? "Entregables por alumno" : "Deliverables each"} value={data?.items_total ?? "-"} testId="teacher-stat-items" />
      </div>

      <ClassManager classes={classes} setClasses={setClasses} classId={classId} setClassId={setClassId} />

      <ClassWeakSpots classId={classId} />

      <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="relative max-w-sm w-full">
        <MagnifyingGlass className="w-4 h-4 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={es ? "Buscar por nombre o email" : "Search by name or email"} className="pl-9 bg-[#121214] border-white/10 text-white" data-testid="teacher-search" />
      </div>
      {data && <TeacherCsvButton data={data} items={items} es={es} className={classes.find((c) => c.class_id === classId)?.name} />}
      </div>

      {!data ? (
        <p className="text-[#71717A]" data-testid="teacher-loading">{es ? "Cargando..." : "Loading..."}</p>
      ) : rows.length === 0 ? (
        <p className="text-[#71717A]" data-testid="teacher-empty">{es ? "No hay estudiantes que mostrar." : "No students to show."}</p>
      ) : (
        <div className="overflow-x-auto border border-white/10 rounded-xl" data-testid="teacher-table">
          <table className="w-full text-sm">
            <thead className="bg-[#121214] text-[#A1A1AA]">
              <tr>
                <th className="text-left font-medium px-4 py-3 sticky left-0 bg-[#121214] min-w-[200px]">{es ? "Estudiante" : "Student"}</th>
                <th className="text-left font-medium px-3 py-3 whitespace-nowrap">{es ? "Proyectos" : "Projects"}</th>
                <th className="text-left font-medium px-3 py-3 whitespace-nowrap">{es ? "Entregables" : "Deliverables"}</th>
                <th className="text-left font-medium px-3 py-3 whitespace-nowrap">{es ? "Insignias" : "Badges"}</th>
                <th className="text-left font-medium px-3 py-3 whitespace-nowrap">{es ? "Mejor examen" : "Best exam"}</th>
                <th className="text-left font-medium px-3 py-3 whitespace-nowrap">{es ? "Certificado" : "Certificate"}</th>
                <th className="text-left font-medium px-3 py-3 whitespace-nowrap">{es ? "Práctica" : "Practice"}</th>
                {items.map((p) => (
                  <th key={p.id} className="text-center font-medium px-2 py-3 text-xs whitespace-nowrap" title={p.title}>
                    {p.title.replace(/ (Photography|Photo)$/i, "").replace(/^Fotografía (de |en )?/i, "")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.email} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`teacher-row-${r.email}`}>
                  <td className="px-4 py-2.5 sticky left-0 bg-[#0A0A0A]">
                    <p className="text-white">{r.name || "-"}</p>
                    <p className="text-[#71717A] text-xs">{r.email}</p>
                    {r.class_name && <p className="text-[#F5A524]/80 text-[11px]" data-testid={`teacher-class-${r.email}`}>{r.class_name}</p>}
                  </td>
                  <td className="px-3 py-2.5 font-['JetBrains_Mono'] text-white whitespace-nowrap">{r.projects_done}/{items.length}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className="font-['JetBrains_Mono'] text-white">{r.items_done}/{data.items_total}</span>
                    <div className="mt-1 h-1 w-20 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#F5A524]" style={{ width: `${data.items_total ? (r.items_done / data.items_total) * 100 : 0}%` }} />
                    </div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap font-['JetBrains_Mono'] text-emerald-300" data-testid={`teacher-badges-${r.email}`}>
                    {r.badges ?? 0}/{data.badges_total ?? 27}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap font-['JetBrains_Mono']" data-testid={`teacher-exam-${r.email}`}>
                    {r.best_exam == null ? (
                      <span className="text-[#3F3F46]">-</span>
                    ) : (
                      <span className={r.best_exam >= Math.ceil((data.exam_total || 30) * 0.8) ? "text-emerald-300" : "text-[#F5A524]"}>
                        {r.best_exam}/{data.exam_total ?? 30}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-[#D4D4D8] text-xs" data-testid={`teacher-cert-${r.email}`}>
                    {r.certificate_date ? new Date(r.certificate_date).toLocaleDateString(es ? "es" : "en", { year: "numeric", month: "short", day: "numeric" }) : <span className="text-[#3F3F46]">-</span>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap font-['JetBrains_Mono'] text-sky-300" data-testid={`teacher-practice-${r.email}`}>
                    {r.practice_clears ? `×${r.practice_clears}` : <span className="text-[#3F3F46]">-</span>}
                  </td>
                  {items.map((p) => (
                    <td key={p.id} className="px-2 py-2.5 text-center">
                      <Cell n={r.projects[p.id] || 0} size={data.sizes[p.id] || 0} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
